// ============================================================
// Der einzige Weg zum Backend.
//
// Fünf Festlegungen stecken darin, und alle fünf haben einen Grund:
//
// 1. RELATIVE Adresse unter /api. Traefik führt /api auf das Backend und
//    schneidet das Präfix ab; bei `pnpm dev` tut dasselbe der Proxy in
//    vite.config.ts. Ein absoluter Host stünde in der gebauten Datei und
//    wäre in der zweiten Umgebung falsch — es gibt hier bewusst kein
//    VITE_API_URL.
// 2. cache: "no-store". Ohne die Zeile wendet der Browser auf Antworten ohne
//    Cache-Control eine GESCHÄTZTE Haltbarkeit aus Last-Modified an. Ein
//    Projektstand aus dem Browsercache ist kein Projektstand.
// 3. credentials: "same-origin". Das Sitzungscookie `__Host-lp_sitzung` ist
//    SameSite und HttpOnly; JavaScript sieht es nie, es muss aber
//    mitgeschickt werden. "include" wäre hier falsch verstandene
//    Großzügigkeit — es gibt keinen fremden Ursprung, der uns etwas angeht.
// 4. Kopfzeile `X-LP: 1` auf jeder VERÄNDERNDEN Anfrage (SPEC §1). Das ist
//    der CSRF-Riegel des Hauses: ein Formular von fremdem Grund kann eigene
//    Kopfzeilen nicht setzen, ohne eine Vorabanfrage auszulösen — und die
//    scheitert, weil das Backend kein CORS spricht. Das Backend weist jede
//    verändernde Anfrage ohne diese Kopfzeile ab. Der Riegel hängt an der
//    METHODE und nicht am Aufrufer: wer eine neue Route ergänzt, kann ihn
//    nicht vergessen.
// 5. Ein 401 setzt den Anmeldespeicher zurück — aber über einen ANGEMELDETEN
//    Rückruf, nicht über einen Import. `store/useAuthStore.ts` ruft das
//    Backend (abmelden, /me) und müsste diese Datei importieren; ein Import
//    zurück wäre ein Kreis, und Kreise brechen in genau der Reihenfolge, die
//    der Bündler zufällig wählt. Deshalb registriert der Speicher sich hier
//    beim Laden.
// ============================================================
import type { Fehler } from "./types";

const BASIS = "/api";

/** Methoden, die etwas ändern — sie bekommen den CSRF-Riegel. */
const VERAENDERND = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiFehler extends Error {
  /** HTTP-Status; 0 bedeutet: die Anfrage kam gar nicht erst an (Netz, Abbruch). */
  readonly status: number;
  /** Der deutsche Satz aus `{"detail": …}` — oder unser Ersatz. Gleich `message`. */
  readonly detail: string;
  /** Der ausgewertete Antwortkörper. Bei 409 steht darin der aktuelle `Projekt`. */
  readonly body: unknown;
  /** Die aufgerufene Adresse, damit eine Meldung ohne Zusatzarbeit brauchbar ist. */
  readonly url: string;

  constructor(status: number, detail: string, body: unknown, url: string) {
    super(detail);
    this.name = "ApiFehler";
    this.status = status;
    this.detail = detail;
    this.body = body;
    this.url = url;
  }

  /** true bei „nicht angemeldet". */
  get istAbgemeldet(): boolean {
    return this.status === 401;
  }

  /** true bei „inzwischen geändert" — der Aufrufer liest dann `body` als `Projekt`. */
  get istKonflikt(): boolean {
    return this.status === 409;
  }
}

/* --------------------------------------------------------- 401-Registrierung */

let beiAbmeldung: (() => void) | null = null;

/**
 * Wird EINMAL von `store/useAuthStore.ts` beim Laden des Moduls gerufen.
 * Danach setzt jeder 401 aus irgendeinem Aufruf den Anmeldestatus auf
 * „anonym", und der Wächter in App.tsx führt zur Anmeldung — ohne Toast: ein
 * abgelaufenes Cookie ist kein Fehler.
 */
export function setzeAbmeldeHandler(fn: () => void): void {
  beiAbmeldung = fn;
}

/* ------------------------------------------------------------------ Meldungen */

const NICHT_ERREICHBAR = "Server nicht erreichbar — bitte neu laden.";

/** Deutsche Standardsätze für die Fälle, in denen der Server keinen mitschickt. */
function standardMeldung(status: number): string {
  if (status === 0) return "Keine Verbindung zum Server. Bist du noch im Netz?";
  if (status === 400) return "Die Eingabe hat der Server nicht angenommen.";
  if (status === 401) return "Du bist nicht (mehr) angemeldet.";
  if (status === 403) return "Dazu fehlt dir die Berechtigung.";
  if (status === 404) return "Das gibt es nicht (mehr).";
  if (status === 409) return "Das wurde zwischenzeitlich geändert.";
  if (status === 413) return "Die Datei ist zu groß.";
  if (status === 415) return "Dieses Dateiformat nehmen wir nicht an.";
  if (status === 422) return "Die Eingabe passt nicht.";
  if (status === 429) return "Zu viele Versuche. Bitte kurz warten.";
  if (status === 503) return "Der Server ist gerade nicht erreichbar. Gleich noch einmal versuchen.";
  if (status >= 500) return "Der Server hat einen Fehler gemeldet.";
  return `Die Anfrage ist fehlgeschlagen (${status}).`;
}

/**
 * Meldung aus dem Antwortkörper ziehen. FastAPI schreibt `detail`, unser
 * nginx-503 ebenfalls. Ist `detail` eine Liste (FastAPIs Prüffehler), wird
 * daraus eine lesbare Zeile.
 */
function meldungAus(body: unknown, status: number): string {
  if (typeof body === "string" && body.trim() && !body.trim().startsWith("<")) return body.trim();
  if (body && typeof body === "object") {
    const o = body as Record<string, unknown>;
    if (typeof o.detail === "string" && o.detail.trim()) return o.detail.trim();
    if (Array.isArray(o.detail)) {
      const teile = o.detail
        .map((d) => (d && typeof d === "object" && typeof (d as Record<string, unknown>).msg === "string" ? (d as Record<string, string>).msg : null))
        .filter(Boolean);
      if (teile.length) return teile.join(" · ");
    }
  }
  return standardMeldung(status);
}

/** Körper auswerten, ohne an einer leeren oder nicht-JSON-Antwort zu zerbrechen. */
async function koerperLesen(res: Response): Promise<unknown> {
  const typ = res.headers.get("content-type") ?? "";
  const roh = await res.text();
  if (!roh) return null;
  if (typ.includes("json")) {
    try {
      return JSON.parse(roh);
    } catch {
      return roh;
    }
  }
  return roh;
}

function adresse(pfad: string): string {
  if (pfad.startsWith("http://") || pfad.startsWith("https://")) return pfad;
  return BASIS + (pfad.startsWith("/") ? pfad : "/" + pfad);
}

export interface AnfrageOpts extends Omit<RequestInit, "body"> {
  /** wird als JSON gesendet; für FormData bitte `api.upload()` nehmen */
  json?: unknown;
  /** roher Körper (FormData, Blob, string) — dann setzt diese Funktion KEIN content-type */
  body?: BodyInit | null;
}

/**
 * Eine Anfrage ans Backend. Gibt den ausgewerteten Körper zurück; bei 204
 * oder leerem Körper `undefined` (als T durchgereicht — wer nichts erwartet,
 * ruft `api.del<void>(…)`).
 *
 * Wirft bei jedem Status ≥ 400 und bei Netzfehlern einen `ApiFehler` mit
 * einer deutschen `detail`, die direkt in einen Toast passt.
 */
export async function anfrage<T = unknown>(pfad: string, opts: AnfrageOpts = {}): Promise<T> {
  const { json, body, headers, method, ...rest } = opts;
  const m = (method ?? (json !== undefined || body !== undefined ? "POST" : "GET")).toUpperCase();
  const h = new Headers(headers);
  h.set("Accept", "application/json");
  if (VERAENDERND.has(m)) h.set("X-LP", "1");
  if (json !== undefined) h.set("Content-Type", "application/json");
  // Bei FormData setzt der Browser den content-type selbst — mit der
  // Trennmarke, die nur er kennt. Ein eigener Wert hier macht den Upload
  // unlesbar; darum steht hier keiner.

  const url = adresse(pfad);
  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      method: m,
      headers: h,
      cache: "no-store",
      credentials: "same-origin",
      body: json !== undefined ? JSON.stringify(json) : (body ?? null),
    });
  } catch (e) {
    // Ein Abbruch über AbortController ist KEIN Fehler der Anwendung — der
    // Aufrufer hat selbst abgebrochen und will seinen eigenen Fehler sehen.
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiFehler(0, standardMeldung(0), null, url);
  }

  if (res.status === 204 || res.status === 205) return undefined as T;

  const koerper = await koerperLesen(res);

  if (!res.ok) {
    // Der 401 zuerst, vor dem Werfen: sonst hängt es am Aufrufer, ob der
    // Anmeldestatus nachgeführt wird, und genau das wird irgendwo vergessen.
    if (res.status === 401 && beiAbmeldung) beiAbmeldung();
    throw new ApiFehler(res.status, meldungAus(koerper, res.status), koerper, url);
  }

  // Ein 200 OHNE JSON ist kein Erfolg, sondern ein Irrläufer: nginx liefert
  // die index.html aus, wenn /api ins Leere zeigt, und `stand.projekte` wäre
  // darauf `undefined` — der Bildschirm bliebe leer, ohne dass irgendwo ein
  // Fehler steht. Lieber ein ehrlicher Satz.
  if (typeof koerper === "string") throw new ApiFehler(res.status, NICHT_ERREICHBAR, koerper, url);
  return (koerper ?? undefined) as T;
}

/* ----------------------------------------------------- Upload mit Fortschritt */

/** §53: was `api.uploadFortschritt` über Pfad und Formular hinaus nimmt. */
export interface FortschrittOpts {
  /** Je Meldung des Browsers: gesendete und gesamte Bytes des RUMPFS (Formular samt Rahmen). */
  beiFortschritt?: (gesendet: number, gesamt: number) => void;
  /** Abbrechen — die Zusage scheitert dann mit `DOMException` „AbortError“, wie bei `fetch`. */
  signal?: AbortSignal;
}

/**
 * §53 (E53.5): ein Mehrteiler-POST über `XMLHttpRequest`, weil `fetch` keinen
 * Fortschritt des HOCHladens meldet — und ein 500-MB-Video ohne Balken sieht
 * aus wie ein hängender Knopf. Dieselben Regeln wie `anfrage` (Kopf oben):
 * relative Adresse unter /api, `X-LP: 1`, der Keks geht von selbst mit (gleicher
 * Ursprung), ein 401 meldet die Abmeldung, jeder Status ≥ 400 wird ein
 * `ApiFehler` mit dem deutschen Satz aus `detail`, ein 200 ohne JSON ein
 * Irrläufer. Nur POST: einen anderen Upload-Weg gibt es nicht.
 */
export function hochladenMitFortschritt<T = unknown>(pfad: string, form: FormData, opts: FortschrittOpts = {}): Promise<T> {
  const url = adresse(pfad);
  return new Promise<T>((erfuellen, verwerfen) => {
    const abgebrochen = (): DOMException => new DOMException("Abgebrochen", "AbortError");
    if (opts.signal?.aborted) {
      verwerfen(abgebrochen());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("X-LP", "1");
    // Wie bei `upload`: KEIN Content-Type — die Trennmarke setzt der Browser.
    const melden = opts.beiFortschritt;
    if (melden) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) melden(e.loaded, e.total);
      };
    }
    const abbrechen = (): void => xhr.abort();
    opts.signal?.addEventListener("abort", abbrechen, { once: true });
    const fertig = (): void => opts.signal?.removeEventListener("abort", abbrechen);
    xhr.onabort = () => {
      fertig();
      verwerfen(abgebrochen());
    };
    xhr.onerror = () => {
      fertig();
      verwerfen(new ApiFehler(0, standardMeldung(0), null, url));
    };
    xhr.onload = () => {
      fertig();
      const typ = xhr.getResponseHeader("content-type") ?? "";
      const roh = xhr.responseText;
      let koerper: unknown = null;
      if (roh) {
        if (typ.includes("json")) {
          try {
            koerper = JSON.parse(roh);
          } catch {
            koerper = roh;
          }
        } else {
          koerper = roh;
        }
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        if (xhr.status === 401 && beiAbmeldung) beiAbmeldung();
        verwerfen(new ApiFehler(xhr.status, meldungAus(koerper, xhr.status), koerper, url));
        return;
      }
      if (xhr.status === 204 || xhr.status === 205) {
        erfuellen(undefined as T);
        return;
      }
      if (typeof koerper === "string") {
        verwerfen(new ApiFehler(xhr.status, NICHT_ERREICHBAR, koerper, url));
        return;
      }
      erfuellen((koerper ?? undefined) as T);
    };
    xhr.send(form);
  });
}

/**
 * Suchparameter bauen und dabei leere Werte weglassen. Eine Liste wird zu
 * MEHREREN Parametern desselben Namens.
 */
export function frage(params: Record<string, string | number | boolean | string[] | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) {
      for (const e of v) if (e !== "") p.append(k, e);
      continue;
    }
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const api = {
  get: <T = unknown>(pfad: string, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "GET" }),
  post: <T = unknown>(pfad: string, json?: unknown, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "POST", json }),
  put: <T = unknown>(pfad: string, json?: unknown, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "PUT", json }),
  patch: <T = unknown>(pfad: string, json?: unknown, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "PATCH", json }),
  del: <T = void>(pfad: string, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "DELETE" }),
  /**
   * Datei-Upload (multipart). Nimmt fertige FormData und setzt AUSDRÜCKLICH
   * kein Content-Type — der Browser muss die Trennmarke selbst anhängen. Wer
   * hier "multipart/form-data" von Hand setzt, bekommt eine Anfrage, die der
   * Server nicht zerlegen kann; das ist der häufigste Upload-Fehler
   * überhaupt.
   *
   *   const fd = new FormData();
   *   for (const d of dateien) fd.append("dateien", d);
   *   await api.upload<Bild[]>(`/projekte/${id}/bilder`, fd);
   */
  upload: <T = unknown>(pfad: string, form: FormData, opts: AnfrageOpts = {}) =>
    anfrage<T>(pfad, { ...opts, method: opts.method ?? "POST", body: form }),
  /** §53: Upload (POST, multipart) MIT Fortschritt — `hochladenMitFortschritt`. */
  uploadFortschritt: <T = unknown>(pfad: string, form: FormData, opts: FortschrittOpts = {}) =>
    hochladenMitFortschritt<T>(pfad, form, opts),
};

/* ------------------------------------------------------------- Direktadressen
   Alles, was NICHT durch fetch läuft, sondern in ein <img src> oder ein
   <a href> geht. Der Strom läuft trotzdem durchs Backend und prüft die
   Sitzung — es gibt keine presigned URLs nach außen (T8).

   Bild-Adressen baut NUR das Backend (`url_klein` …, `titelbild_url`): seit
   §38.3 tragen sie ein Fassungszeichen `?v=…`, das sich ändert, sobald sich die
   Bildpunkte ändern (Ersetzen, Zuschnitt). Eine Adresse aus der bloßen Kennung
   hätte keins — und zeigte ein Jahr lang das alte Bild aus dem Browserspeicher.
   Darum stehen die zwei Helfer `bildAdresse`/`oeffentlichesBild`, die das
   taten, hier nicht mehr (genutzt hatte sie ohnehin niemand). Wer nur eine
   Kennung hat, holt sich das `Bild`. */

/** Typwächter für `catch`-Zweige, die nur den Körper eines 409 wollen. */
export function istApiFehler(e: unknown): e is ApiFehler {
  return e instanceof ApiFehler;
}

/** Nur zur Sicherheit: `Fehler` ist die Form, die das Backend bei jedem 4xx schickt. */
export type Fehlerkoerper = Fehler;
